import type {
  AdminChangePasswordRequest,
  AdminLoginRequest,
  AdminMe,
  AdminRole,
} from '@lions/contracts';
import { createContext, use } from 'react';

import type { MfaChallenge } from '../api/auth-api';

export type AuthStatus = 'loading' | 'signed-in' | 'signed-out';

/** Why the admin is signed out: `logout` drops the return path, `expired` keeps it. */
export type SignOutReason = 'logout' | 'expired' | null;

export interface AuthContextValue {
  status: AuthStatus;
  me: AdminMe | null;
  signOutReason: SignOutReason;
  /**
   * Signs in and loads `/me`, or resolves the MFA step to show next; rejects with ApiError on
   * wrong credentials or a lockout.
   */
  login: (credentials: AdminLoginRequest) => Promise<MfaChallenge | null>;
  /** Finishes the MFA step with an authenticator or recovery code. */
  completeMfa: (mfaToken: string, code: string) => Promise<void>;
  /** Recovery codes shown once after MFA enrolment, until the admin confirms they saved them. */
  recoveryCodes: string[] | null;
  acknowledgeRecoveryCodes: () => void;
  /** `PATCH /auth/password`: keeps this session (new access token), ends every other one. */
  changePassword: (body: AdminChangePasswordRequest) => Promise<void>;
  /** Ends the session locally at once and revokes it on the server. */
  logout: () => Promise<void>;
  /** True for a SUPER_ADMIN or an admin holding any of `roles` (05 § Admin surface). */
  hasAnyRole: (roles: readonly AdminRole[]) => boolean;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuth(): AuthContextValue {
  const auth = use(AuthContext);
  if (!auth) throw new Error('useAuth must be used inside <AuthProvider>');
  return auth;
}
